import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import user1 from "../assets/images/user/users.png";
import { useDispatch, useSelector } from "react-redux";
import { uploadFileToCloudinary } from "../store/Cloudinary/CloudinaryThunk";
import { addMediaToInstagram, igProfile, publishInstagramStory, publishMediaOnInstagram } from "../store/instagram/instagramThunk";
import { addMediaToFacebook, addMediaToStories } from "../store/facebook/facebookThunk";
import { toast } from "react-toastify";

const CreatePostNew = ({ type, userImage }) => {
  const [show, setShow] = useState(false);
  const [formType, setFormType] = useState("media"); // Default to 'media' for photo/video
  const [selectedFileName, setSelectedFileName] = useState("");

  const { uploadStatus, uploadedFile, uploadError } = useSelector(
    (state) => state.cloudinary
  );
  const { loading, error, message } = useSelector((state) => state.instagram);

  const dispatch = useDispatch();

  const handleClose = () => {
    setShow(false);
    setSelectedFileName("");
  };

  const handleShow = (type) => {
    setFormType(type);
    setShow(true);
  };

  // Validation Schema for Formik
  const validationSchema = () => {
    if (formType === "media") {
      return Yup.object({
        file: Yup.mixed().required("File is required"),
        postText: Yup.string(), // Caption is optional
      });
    } else if (formType === "story") {
      return Yup.object({
        file: Yup.mixed().required("File is required"),
      });
    }
  };

  // Helper function to determine file type
  const getFileType = (file, uploadedUrl) => {
    // Check by file object type first
    if (file && file.type) {
      if (file.type.startsWith('image/')) return 'image';
      if (file.type.startsWith('video/')) return 'video';
    }
    
    // Fallback to URL checking
    if (uploadedUrl) {
      const url = uploadedUrl.toLowerCase();
      if (url.includes("image") || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url)) {
        return 'image';
      }
      if (url.includes("video") || /\.(mp4|mov|avi|wmv|webm|flv)$/i.test(url)) {
        return 'video';
      }
    }
    
    return null;
  };

  // Mock FBProfile function - replace with actual API call
  const fbProfile = () => {
    return new Promise((resolve) => {
      // Replace this alert with actual API call
      alert("FBProfile API should be called here");
      resolve({ success: true });
    });
  };

  const Submitform = async (values, { resetForm }) => {
    const toastId = toast.loading("Processing...");

    try {
      // Upload file to Cloudinary
      const uploadResponse = await dispatch(uploadFileToCloudinary(values.file));
      const uploadedFile = uploadResponse?.payload;

      if (!uploadedFile?.url) {
        toast.update(toastId, {
          render: uploadError || "Failed to upload file!",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }

      // Determine file type
      const fileType = getFileType(values.file, uploadedFile.url);
      
      if (!fileType) {
        toast.update(toastId, {
          render: "Invalid file type! Please upload an image or video.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }

      // Build payload based on form type and platform
      let data = {};
      
      if (formType === "media") {
        // Regular post
        data = {
          media_type: fileType === 'image' ? "IMAGE" : "VIDEO",
          [fileType === 'image' ? "imageUrl" : "videoUrl"]: uploadedFile.url,
        };
        
        if (values.postText && values.postText.trim()) {
          data.caption = values.postText.trim();
        }
      } else if (formType === "story") {
        // Story post
        if (type === "instagram") {
          data = {
            media_type: fileType === 'image' ? "STORY_IMAGE" : "STORY_VIDEO",
            [fileType === 'image' ? "imageUrl" : "videoUrl"]: uploadedFile.url,
          };
        } else if (type === "facebook") {
          // Facebook stories might have different structure
          data = {
            media_type: fileType === 'image' ? "STORY_IMAGE" : "STORY_VIDEO",
            [fileType === 'image' ? "imageUrl" : "videoUrl"]: uploadedFile.url,
          };
        }
      }

      // Post to the platform
      let response;
      try {
        if (type === "instagram") {
          if (formType === "story") {
            response = await dispatch(addMediaToInstagram(data));
          } else {
            response = await dispatch(addMediaToInstagram(data));
          }
        } else if (type === "facebook") {
          if (formType === "story") {
            response = await dispatch(addMediaToStories(data));
          } else {
            response = await dispatch(addMediaToFacebook(data));
          }
        }

        console.log( response,"responseresponseresponseresponse")

        // Handle response
        const payload = response?.payload;
        let isSuccess = false;
        let message = "";

        // Safely extract message and success status
        if (payload && typeof payload === 'object') {
          isSuccess = payload.success === true;
          
          // Extract message safely
          if (typeof payload.message === 'string') {
            message = payload.message;
          } else if (payload.message && typeof payload.message === 'object') {
            // If message is an object, convert to string or use default
            message = JSON.stringify(payload.message);
          } else {
            message = isSuccess ? "Posted successfully!" : "Failed to post!";
          }
        } else {
          message = "Posted successfully!";
          isSuccess = true;
        }

        if (isSuccess) {
          // Call appropriate profile refresh API
          try {
            if (type === "instagram") {
              await dispatch(igProfile()).unwrap();
            } else if (type === "facebook") {
              await fbProfile(); // Call FB profile API
            }
          } catch (profileError) {
            console.error("Profile refresh error:", profileError);
            // Don't fail the whole operation if profile refresh fails
          }

          toast.update(toastId, {
            render: message,
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
        } else {
          toast.update(toastId, {
            render: message,
            type: "error",
            isLoading: false,
            autoClose: 3000,
          });
        }

        resetForm();
        handleClose();
      } catch (postError) {
        console.error("Post submission error:", postError);
        
        let errorMessage = "";
        if (postError?.response?.data?.message) {
          errorMessage = postError.response.data.message;
        } else if (postError?.message) {
          errorMessage = postError.message;
        } else {
          errorMessage = `Failed to post to ${type === "instagram" ? "Instagram" : "Facebook"}!`;
        }

        toast.update(toastId, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      
      let errorMessage = "";
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      } else {
        errorMessage = "An error occurred while processing your request.";
      }

      toast.update(toastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      <div id="post-modal-data" className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h4 className="text-gray-800 font-medium text-lg">Create Post</h4>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-4 mb-4">
            <img
              src={userImage || user1}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover border border-gray-200"
            />
            <button
              onClick={() => handleShow("media")}
              className="flex-1 text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-full text-gray-500 transition-colors cursor-pointer border border-gray-200"
            >
              What's on your mind?
            </button>
          </div>

          <hr className="my-4" />

          <div className="card-body">
            <hr />
            <ul className="post-opt-block d-flex list-inline m-0 p-0 flex-wrap gap-3">
              <li
                className="rounded p-2 pointer d-flex align-items-center text-[#000]"
                onClick={() => handleShow("media")}
              >
                <button
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#50B5FF] transition-colors text-white hover:bg-[#3da3f0]"
                >
                  <span className="font-medium">+ Add Photo/Video</span>
                </button>
              </li>
              <li
                className="rounded p-2 pointer d-flex align-items-center"
                onClick={() => handleShow("story")}
              >
                <button
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#50B5FF] transition-colors text-white hover:bg-[#3da3f0]"
                >
                  <span className="font-medium">+ Add Story</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        id="custom-post-modal"
      >
        <Modal.Header className="border-b border-gray-200 bg-white">
          <Modal.Title className="text-lg font-semibold text-gray-800">
            {formType === "story" ? "Create Story" : "Create Post"}
          </Modal.Title>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </Modal.Header>

        <Modal.Body className="p-6 bg-white">
          <div className="flex items-start space-x-3 mb-4">
            <img
              src={userImage || user1}
              alt={`${type} Profile`}
              className="w-10 h-10 rounded-full object-cover border border-gray-200"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-800 capitalize">{type} {formType === "story" ? "Story" : "Post"}</div>
              <div className="text-sm text-gray-500">Public</div>
            </div>
          </div>

          <Formik
            initialValues={{ postText: "", file: null }}
            validationSchema={validationSchema}
            onSubmit={Submitform}
          >
            {({ setFieldValue, resetForm, values }) => (
              <Form className="space-y-4">
                {formType === "media" && (
                  <>
                    <div>
                      <Field name="postText">
                        {({ field }) => (
                          <textarea
                            {...field}
                            className="w-full p-3 border border-gray-200 rounded-lg focus:border-[#50B5FF] focus:ring-1 focus:ring-[#50B5FF] resize-none text-gray-700 placeholder-gray-400"
                            rows="2"
                            placeholder="Add a caption (optional)"
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="postText"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-[#50B5FF] transition-colors">
                      <input
                        type="file"
                        name="file"
                        id="file-upload"
                        className="hidden"
                        accept="image/*,video/*"
                        onChange={(event) => {
                          const file = event.currentTarget.files[0];
                          setFieldValue("file", file);
                          setSelectedFileName(file?.name || "");
                        }}
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <div className="flex flex-col items-center">
                          <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                          <div className="text-gray-600 font-medium">Add Photo/Video</div>
                          <div className="text-gray-400 text-sm mt-1">Click to upload</div>
                          {selectedFileName && (
                            <div className="mt-2 text-sm text-gray-500">
                              <strong>Selected:</strong> {selectedFileName}
                            </div>
                          )}
                        </div>
                      </label>
                      <ErrorMessage
                        name="file"
                        component="div"
                        className="text-red-500 text-sm mt-2"
                      />
                    </div>
                  </>
                )}

                {formType === "story" && (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-[#50B5FF] transition-colors">
                    <input
                      type="file"
                      name="file"
                      id="file-upload-story"
                      className="hidden"
                      accept="image/*,video/*"
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        setFieldValue("file", file);
                        setSelectedFileName(file?.name || "");
                      }}
                    />
                    <label htmlFor="file-upload-story" className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <div className="text-gray-600 font-medium">Add Story</div>
                        <div className="text-gray-400 text-sm mt-1">Photo or Video</div>
                        {selectedFileName && (
                          <div className="mt-2 text-sm text-gray-500">
                            <strong>Selected:</strong> {selectedFileName}
                          </div>
                        )}
                      </div>
                    </label>
                    <ErrorMessage
                      name="file"
                      component="div"
                      className="text-red-500 text-sm mt-2"
                    />
                  </div>
                )}

                <div className="pt-4 border-t border-gray-100">
                  <Button
                    type="submit"
                    className="w-full bg-[#50B5FF] hover:bg-[#3da3f0] border-0 py-2.5 rounded-lg font-medium transition-colors text-white"
                    disabled={loading || uploadStatus === 'loading'}
                  >
                    {loading || uploadStatus === 'loading' 
                      ? "Publishing..." 
                      : formType === "story" ? "Publish Story" : "Publish Post"
                    }
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      <div
        className={`modal-backdrop fade ${show ? "show" : "d-none"}`}
        onClick={handleClose}
      ></div>
    </>
  );
};

export default CreatePostNew;






// import React, { useEffect, useState } from "react";
// import { Link } from "react-router-dom";
// import { Dropdown, Button, Modal } from "react-bootstrap";
// import { Formik, Form, Field, ErrorMessage } from "formik";
// import * as Yup from "yup";
// // import user1 from "../assets/images/user/1.jpg";
// import user1 from "../assets/images/user/users.png";
// import small1 from "../assets/images/small/07.png";
// import small2 from "../assets/images/small/08.png";
// import small3 from "../assets/images/small/09.png";
// import small4 from "../assets/images/small/10.png";
// import small5 from "../assets/images/small/11.png";
// import small6 from "../assets/images/small/12.png";
// import small7 from "../assets/images/small/13.png";
// import small8 from "../assets/images/small/14.png";
// import { useDispatch, useSelector } from "react-redux";
// import { uploadFileToCloudinary } from "../store/Cloudinary/CloudinaryThunk";
// import { addMediaToInstagram } from "../store/instagram/instagramThunk";
// import swal from "sweetalert";
// import { addMediaToFacebook, addMediaToStories } from "../store/facebook/facebookThunk";
// import UserList from "./sharePostModel/user-list-model";

// const CreatePostNew = ({ type, userImage,handleshowStory }) => {
//   const [show, setShow] = useState(false);
//   const [formType, setFormType] = useState("text"); // Default to 'text'
//   const [caption, setCaption] = useState(); // Default to 'text'

//   const { uploadStatus, uploadedFile, uploadError } = useSelector(
//     (state) => state.cloudinary
//   );
//   const { loading, error, message } = useSelector((state) => state.instagram);

//   console.log(
//     uploadStatus,
//     uploadedFile,
//     uploadError,
//     "uploadStatus, uploadedFile, uploadError"
//   );

//   console.log(
//     loading,
//     error,
//     message,
//     "----------------loading, error,message-------------------"
//   );
//   const dispatch = useDispatch();
//   handleshowStory = ()=>{
//     setFormType('only/Story')
//     setShow(true)
//   }
//   const handleClose = () => setShow(false);

//   const handleShow = (type) => {
//     alert(type)
//     setFormType(type);
//     setShow(true);
//   };

//  useEffect(() => {
//   if (uploadedFile?.url) {
//     let data = null; // Initialize data as null

//     console.log(uploadedFile, "---------------------uploadedFile-------------");

//     switch (formType) {
//       case "Photo/text":
//         data = {
//           imageUrl: uploadedFile.url,
//           caption: caption,
//         };
//         break;

//       case "only/Photo":
//         if (uploadedFile.url.includes("image")) {
//           data = { imageUrl: uploadedFile.url };
//         } else if (uploadedFile.url.includes("video")) {
//           alert("This is the video");
//           data = {
//             videoUrl: uploadedFile.url,
//             type: "REELS",
//             media_type: "REELS",
//           };
//         }
//         break;

//       case "only/Story":
//         if (uploadedFile.url.includes("video")) {
//           alert("This is the video story");
//           data = {
//             videoUrl: uploadedFile.url,
//             type: "REELS",
//             media_type: "STORIES",
//           };
//         } else {
//           data = {
//             imageUrl: uploadedFile.url,
//             media_type: "STORIES",
//           };
//         }
//         break;

//       default:
//         console.error("Invalid formType:", formType);
//         break;
//     }

//     // Dispatch if data is prepared
//     if (data) {
//       if (type === "instagram") {
//         dispatch(addMediaToInstagram(data));
//         handleClose();
//       } else if (type === "facebook") {
//         if (formType === "only/Story") {
//           dispatch(addMediaToStories(data));
//         } else {
//           dispatch(addMediaToFacebook(data));
//         }
//         handleClose();
//       }
//     }
//   }
// }, [uploadedFile]);


//   // Validation Schema for Formik
//   const validationSchema = () => {
//     if (formType === "text") {
//       return Yup.object({
//         postText: Yup.string().required("Post content is required"),
//       });
//     } else if (formType === "Photo/text") {
//       return Yup.object({
//         postText: Yup.string().required("Post content is required"),

//         file: Yup.mixed().required("File is required"),
//       });
//     } else {
//       return Yup.object({
//         file: Yup.mixed().required("File is required"),
//       }); // Return an empty schema if no conditions match
//     }
//   };

//   const Submitform = (values) => {
//     if (formType === "Photo/text") {
//       setCaption(values?.postText);
//       swal({
//         title: "Uploading...",
//         text: "Please wait while your image is being uploaded.",
//         allowOutsideClick: false,
//         didOpen: () => {
//           swal.showLoading();
//         },
//       });

//       dispatch(uploadFileToCloudinary(values.file))
//         .then(() => {
//           // Close the SweetAlert after the upload completes
//           setCaption(values?.postText);
//           swal.close();
//         })
//         .catch((error) => {
//           // Handle any errors and show error alert
//           swal.fire({
//             icon: "error",
//             title: "Upload Failed",
//             text: "Something went wrong while uploading the image.",
//           });
//         });
//     } else if (formType === "only/Photo" || formType==="only/Story") {
//       swal({
//         title: "Uploading...",
//         text: "Please wait while your image is being uploaded.",
//         allowOutsideClick: false,
//         didOpen: () => {
//           swal.showLoading();
//         },
//       });

//       dispatch(uploadFileToCloudinary(values.file))
//         .then(() => {
//           // Close the SweetAlert after the upload completes
//           swal.close();
//         })
//         .catch((error) => {
//           // Handle any errors and show error alert
//           swal.fire({
//             icon: "error",
//             title: "Upload Failed",
//             text: "Something went wrong while uploading the image.",
//           });
//         });
//     } else if (formType === "text") {
//       let data = {
//         caption: values?.postText,
//       };
//       dispatch(addMediaToFacebook(data));
//     }

//     // handleClose(); // Close the modal after submission
//   };

//   return (
//     <>
//       <div id="post-modal-data" className={`card `}>
//         <div className="card-header d-flex justify-content-between">
//           <div className="header-title">
//             <h4 className="card-title">Create Post</h4>
//           </div>
//         </div>
//         <div className="card-body">
//           {/* <div className="d-flex align-items-center">
//             <div className="user-img">
//               <img
//                 loading="lazy"
//                 src={user1}
//                 alt="userimg"
//                 className="avatar-60 rounded-circle"
//               />
//             </div>
//           </div> */}
//           <hr />
//           <ul className="post-opt-block d-flex list-inline m-0 p-0 flex-wrap gap-3">
//             <li
//               className="bg-primary-subtle rounded p-2 pointer d-flex align-items-center"
//               onClick={() => handleShow("Photo/text")}
//             >
//               <img
//                 loading="lazy"
//                 src={small1}
//                 alt="icon"
//                 className="img-fluid me-2"
//               />
//               Photo/text
//             </li>

//             <li
//               className="bg-primary-subtle rounded p-2 pointer d-flex align-items-center"
//               onClick={() => handleShow("only/Photo")}
//             >
//               <img
//                 loading="lazy"
//                 src={small3}
//                 alt="icon"
//                 className="img-fluid me-2"
//               />
//               only/Photo
//             </li>
//             <li
//               className="bg-primary-subtle rounded p-2 pointer d-flex align-items-center"
//               onClick={() => handleShow("only/Story")}
//             >
//               <img
//                 loading="lazy"
//                 src={small3}
//                 alt="icon"
//                 className="img-fluid me-2"
//               />
//               only/Story
//             </li>
//             {/* <li className="bg-primary-subtle rounded p-2 pointer text-center">
//               <div className="card-header-toolbar d-flex align-items-center">
//                 <Dropdown>
//                   <Dropdown.Toggle as="div" id="post-option" className="lh-1">
//                     <span className="material-symbols-outlined">
//                       more_horiz
//                     </span>
//                   </Dropdown.Toggle>
//                   <Dropdown.Menu
//                     className="dropdown-menu-right"
//                     aria-labelledby="post-option"
//                   >
//                     <Dropdown.Item onClick={() => handleShow("checkin")}>
//                       Check in
//                     </Dropdown.Item>
//                     <Dropdown.Item onClick={() => handleShow("live")}>
//                       Live Video
//                     </Dropdown.Item>
//                     <Dropdown.Item onClick={() => handleShow("gif")}>
//                       Gif
//                     </Dropdown.Item>
//                     <Dropdown.Item onClick={() => handleShow("watchparty")}>
//                       Watch Party
//                     </Dropdown.Item>
//                     <Dropdown.Item onClick={() => handleShow("play")}>
//                       Play with Friend
//                     </Dropdown.Item>
//                   </Dropdown.Menu>
//                 </Dropdown>
//               </div>
//             </li> */}
//           </ul>
//         </div>

//         {/* Modal */}
//         <Modal
//           show={show}
//           onHide={handleClose}
//           size="lg"
//           centered
//           fullscreen="sm-down"
//           id="custom-post-modal"
//         >
//           <div className="modal-header d-flex justify-content-between">
//             <h5 className="modal-title" id="post-modalLabel">
//               Create Post
//             </h5>
//             <Link to="#" className="lh-1" onClick={handleClose}>
//               <span className="material-symbols-outlined">close</span>
//             </Link>
//           </div>
//           <Modal.Body>
//             <div className="d-flex align-items-center">
//               <div className="user-img">
//                 {type === 'instagram' ? (
//                   <img
//                     src={userImage} // Replace with Instagram image URL or path
//                     alt="Instagram Platform"
//                     className="avatar-60 rounded-circle"
//                   />
//                 ) : type === 'facebook' ? (
//                   <img
//                     src={userImage} // Replace with Facebook image URL or path
//                     alt="Facebook Platform"
//                     className="avatar-60 rounded-circle"
//                   />
//                 ) : (
//                   <p>Select a platform</p>
//                 )}
//               </div>
//               <Formik
//                 initialValues={{ postText: "", file: null }}
//                 validationSchema={validationSchema(formType)} // Pass formType to validationSchema
//                 onSubmit={Submitform}
//               >
//                 {({ setFieldValue }) => (
//                   <Form className="post-text ms-3 w-100">
//                     {formType === "only/Photo" && (
//                       <div className="mt-3">
//                         <h6>Photo/Video Post</h6>
//                         <input
//                           type="file"
//                           name="file"
//                           className="form-control"
//                           accept="image/*,video/*"
//                           onChange={(event) => {
//                             setFieldValue("file", event.currentTarget.files[0]);
//                           }}
//                         />
//                         <ErrorMessage
//                           name="file"
//                           component="div"
//                           className="text-danger"
//                         />
//                       </div>
//                     )}
//                       {formType === "only/Story" && (
//                       <div className="mt-3">
//                         <h6>Photo/Video Post</h6>
//                         <input
//                           type="file"
//                           name="file"
//                           className="form-control"
//                           accept="image/*,video/*"
//                           onChange={(event) => {
//                             setFieldValue("file", event.currentTarget.files[0]);
//                           }}
//                         />
//                         <ErrorMessage
//                           name="file"
//                           component="div"
//                           className="text-danger"
//                         />
//                       </div>
//                     )}
//                     {formType === "Photo/text" && (
//                       <div className="mt-3">
//                         <h6>Image/Video with Text</h6>
//                         <input
//                           type="file"
//                           name="file"
//                           className="form-control mb-2"
//                           accept="image/*,video/*"
//                           onChange={(event) => {
//                             setFieldValue("file", event.currentTarget.files[0]);
//                           }}
//                         />
//                         <ErrorMessage
//                           name="file"
//                           component="div"
//                           className="text-danger"
//                         />
//                         <Field
//                           as="textarea"
//                           name="postText"
//                           className="form-control"
//                           rows="3"
//                           placeholder="Write something with your image/video..."
//                         />
//                         <ErrorMessage
//                           name="postText"
//                           component="div"
//                           className="text-danger"
//                         />
//                       </div>
//                     )}
//                     {formType === "text" && (
//                       <div className="mt-3">
//                         <h6>Only Text</h6>
//                         <Field
//                           as="textarea"
//                           name="postText"
//                           className="form-control"
//                           rows="3"
//                           placeholder="Write something..."
//                         />
//                         <ErrorMessage
//                           name="postText"
//                           component="div"
//                           className="text-danger"
//                         />
//                       </div>
//                     )}
//                     <Button
//                       variant="primary"
//                       type="submit"
//                       className="d-block w-100 mt-3"
//                     >
//                       Post
//                     </Button>
//                   </Form>
//                 )}
//               </Formik>
//             </div>
//           </Modal.Body>
//         </Modal>
//       </div>
//       <div
//         className={`modal-backdrop fade ${show ? "show" : "d-none"}`}
//         onClick={handleClose}
//       ></div>
//     </>
//   );
// };

// export default CreatePostNew;